import appStore from '../utils/state';
import gsap from 'gsap';

export default class HeaderSearchForm extends HTMLElement {
  private form: HTMLFormElement | null = null;
  private input: HTMLInputElement | null = null;
  private icon: HTMLSpanElement | null = null;

  private width: number = 0;

  private isVisible: boolean = false;

  private unsubscribeFromIsSearchModalOpen: (() => void) | null = null; // Is Search Modal Open

  private onSearchModalUpdateBound = this.onSearchModalUpdate.bind(this);
  private onResizeBound = this.onResize.bind(this); // Resize
  private onInputBound = this.onInput.bind(this); // Input
  private onFormSubmitBound = this.onFormSubmit.bind(this); // Form Submit

  private searchShowTl: gsap.core.Timeline | null = null;
  private searchHideTl: gsap.core.Timeline | null = null;

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.form = this.querySelector('[data-header-search-form-ref="form"]');
    this.input = this.querySelector('[data-header-search-form-ref="input"]');
    this.icon = this.querySelector('[data-header-search-form-ref="icon"]');

    this.addEventListeners();
    this.subscribe();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);
    this.onResize();

    if (this.input) {
      this.input.addEventListener('input', this.onInputBound);
    }

    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmitBound);
    }
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);

    if (this.form) {
      this.form.removeEventListener('submit', this.onFormSubmitBound);
    }
  }

  subscribe() {
    this.unsubscribeFromIsSearchModalOpen = appStore.subscribeKey('isSearchModalOpen', this.onSearchModalUpdateBound);
  }

  unsubscribe() {
    if (this.unsubscribeFromIsSearchModalOpen) {
      this.unsubscribeFromIsSearchModalOpen();
      this.unsubscribeFromIsSearchModalOpen = null;
    }
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    appStore.actions.updateSearchValue(input.value);
  }

  onFormSubmit(event: SubmitEvent): void {
    const { isSearchRedirect, searchRedirectUrl } = appStore.getState();
    if (!isSearchRedirect || !searchRedirectUrl) {
      return;
    }

    event.preventDefault();
    window.location.assign(searchRedirectUrl);
  }

  onResize(): void {
    const rect = this.getBoundingClientRect();
    this.width = rect.width;
  }

  onSearchModalUpdate(isSearchModalOpen: boolean): void {
    this.isVisible = isSearchModalOpen;

    if (this.isVisible) {
      this.onSearchShow();
    } else {
      this.onSearchHide();
    }
  }

  onSearchShow(): void {
    if (this.searchShowTl) {
      this.searchShowTl.kill();
    }

    if (this.searchHideTl) {
      this.searchHideTl.kill();
    }

    this.searchShowTl = gsap.timeline();
    this.searchShowTl.set(
      this,
      {
        autoAlpha: 1,
      },
      0,
    );
    this.searchShowTl.fromTo(
      this.icon,
      {
        x: this.width + 8,
      },
      {
        duration: 0.6,
        x: 0,
        ease: 'expo.out',
      },
      0,
    );
    this.searchShowTl.to(
      this.input,
      {
        duration: 0.3,
        autoAlpha: 1,
        ease: 'power2.out',
      },
      0.3,
    );
    this.searchShowTl.add(() => {
      if (this.input) {
        this.input.focus();
      }
    }, 0.4);
  }

  onSearchHide(): void {
    if (this.searchHideTl) {
      this.searchHideTl.kill();
    }

    this.searchHideTl = gsap.timeline();

    this.searchHideTl.to(
      this.input,
      {
        duration: 0.1,
        autoAlpha: 0,
        ease: 'power2.out',
      },
      0,
    );
    this.searchHideTl.to(
      this.icon,
      {
        duration: 0.6,
        x: this.width + 8,
        ease: 'expo.out',
      },
      0,
    );
    this.searchHideTl.set(
      this,
      {
        autoAlpha: 0,
      },
      0.6,
    );
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();
  }
}

customElements.define('header-search-form', HeaderSearchForm);
